import React from 'react';
import ReactDOM from 'react-dom/client';
import { 
  init, 
  browserTracingIntegration,  
  replayIntegration,
} from "@sentry/react";
import './index.css';
import App from './App';
import { Provider } from 'react-redux';
import { store } from './store';
import axios from 'axios';
import { logoutAsync } from './slices/authSlice';
import { requestManager } from './helpers/requestManager';

init({
  dsn: process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_SENTRY_DSN
    : undefined,
  environment: process.env.NODE_ENV || 'development',
  sendDefaultPii: true,
  integrations: [
    browserTracingIntegration(),
    replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

axios.interceptors.request.use(
  (config) => {
    const method = (config.method || 'get').toLowerCase();
    const url = config.url || '';

    // Auto-cancel previous "same endpoint" GET when a new one starts with new params.
    // This centralizes "params changed => cancel previous" behavior without per-thunk wrappers.
    if (
      method === 'get' &&
      !config.signal
    ) {
      const basePath = url.split('?')[0];
      const key = `get:${basePath}`;
      const currentParams = config.params || {};
      const originalPage = currentParams.page || 1;

      // Clear any stale silent flags if this is a user-initiated request on page 1
      // (not a system-driven page reset)
      if (originalPage === 1) {
        requestManager.clearStaleSilentFlag(key);
      }

      // Check if non-page params changed and reset page to 1 if needed
      const { paramsChanged, updatedParams } = requestManager.checkAndResetPageIfParamsChanged(key, currentParams);
      if (paramsChanged) {
        // Only set the silent flag if page was actually changed FROM something other than 1 TO 1
        // If user is already on page 1, don't set the flag (they should be able to make requests)
        const wasPageChanged = originalPage !== 1;
        
        // Only modify config.params - axios will build the query string from this
        config.params = updatedParams;
        // Store flag to update browser URL in response interceptor
        // Only mark as reset if page was actually changed (not if already on page 1)
        if (wasPageChanged) {
          config._pageWasReset = true;
          config._resetPageValue = 1;
          config._pageResetKey = key;
        }
      }

      // Get signal and track params
      config.signal = requestManager.nextSignalForKey(key, updatedParams);
      return config;
    }

    if (!config.signal) {
      config.signal = requestManager.currentAbortableThunkSignal || requestManager.signal;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isLoggingOut = false;
axios.interceptors.response.use(
  (response) => {
    // Update browser URL and emit event if page was reset in the request
    const config = response.config;
    if (config?._pageWasReset && config?._resetPageValue !== undefined) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('page', String(config._resetPageValue));
      window.history.replaceState({}, '', currentUrl.toString());
      // Emit page reset event so components can update their state
      requestManager.emitPageReset(config?._pageResetKey);
    }
    return response;
  },
  async (error) => {
    if (error?.response?.status === 401 && !isLoggingOut) {
      isLoggingOut = true;
      try {
        await store.dispatch(logoutAsync());
      } finally {
        isLoggingOut = false;
      }
    }
    return Promise.reject(error);
  }
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <Provider store={store}>
    <App />
  </Provider>
);
